/**
 * Enterprise SaaS Design System Brand Colors
 *
 * This file contains the official brand color definitions following the blue-based palette
 * used in the vue.frontends project. These colors follow a steel blue-based approach with
 * strategic use of supporting colors.
 * All colors are WCAG 2.1 AA compliant for accessibility.
 *
 * USAGE GUIDELINES:
 * - Steel Blue (#406A94) for primary actions, toggles, and radios
 * - Prussian Blue (#0B3142) for CSP accordion headers only
 * - Cadet Gray (#7F8C98) for tertiary buttons
 * - Chart colors use the specialized gray scale system
 * - For theming and custom properties, use CSS variables from base.scss
 *
 * @see DEVELOPMENT_CONVENTIONS.md for detailed usage guidelines
 * @see semanticTokens.ts for semantic color mappings
 */

/**
 * Core brand colors - the foundation of the design system
 */
export const BRAND_COLORS = {
  /** Primary brand steel blue - primary actions, toggles, radios */
  primary: "#406A94",
  /** Secondary prussian blue - CSP accordion headers only */
  secondary: "#0B3142", 
  /** Tertiary cadet gray - tertiary buttons */
  tertiary: "#7F8C98",
  /** Pure white - content backgrounds, cards, and high-contrast elements */
  white: "#ffffff",
} as const;

/**
 * Extended color scales for more nuanced design needs
 */
export const BRAND_SCALES = {
  steelBlue: {
    base: "#406A94",
    dark: "#355a80",
    light: "#5a7ba8",
    veryLight: "#e8f0f8",
  },
  prussianBlue: {
    base: "#0B3142",
    dark: "#082735",
    light: "#1a4a5e",
    veryLight: "#e1e8ed",
  },
  cadetGray: {
    base: "#7F8C98",
    dark: "#6b7884",
    light: "#939fac",
    veryLight: "#f0f2f4",
  },
  coolGray: {
    base: "#7D8CA3", // Charts only
    dark: "#6a7a91",
    light: "#919fb5",
    veryLight: "#f0f2f5",
  },
  battleshipGray: {
    base: "#969A97", // Charts only
    dark: "#818581",
    light: "#abaead",
    veryLight: "#f2f3f2",
  },
  blackOlive: {
    base: "#334139", // Charts only
    dark: "#29342f",
    light: "#455a4d",
    veryLight: "#e7ebe8",
  },
  onyx: {
    base: "#3C3C3C", // Charts only
    dark: "#333333",
    light: "#525252",
    veryLight: "#e8e8e8",
  },
} as const;

/**
 * Chart and data visualization colors
 * Enterprise SaaS Design System chart colors
 *
 * Based on the Enterprise SaaS Design System specification:
 * - Steel Blue (#406A94) - Primary action colors
 * - Prussian Blue (#0B3142) - CSP accordion headers only
 * - Cadet Gray (#7F8C98) - Tertiary buttons
 * - Cool Gray (#7D8CA3) - Charts only
 * - Battleship Gray (#969A97) - Charts only  
 * - Black Olive (#334139) - Charts only
 * - Onyx (#3C3C3C) - Charts only
 */
export const CHART_COLORS = {
  primary: "#406A94", // Steel Blue - primary data series
  secondary: "#7D8CA3", // Cool Gray - secondary data series (charts only)
  tertiary: "#969A97", // Battleship Gray - tertiary data series (charts only)
  quaternary: "#334139", // Black Olive - quaternary data series (charts only)
  neutral: "#3C3C3C", // Onyx - neutral data series (charts only)
  accent: "#0B3142", // Prussian Blue - accent data (limited use)
  positive: "#059669", // Green - positive trends, success (keeping semantic color)
  negative: "#dc2626", // Red - negative trends, errors (keeping semantic color)
  warning: "#ea580c", // Orange - warnings (keeping semantic color)
} as const;

/**
 * Extended chart colors for complex visualizations (6+ data series)
 */
export const EXTENDED_CHART_COLORS = {
  ...CHART_COLORS,
  cadetGray: "#7F8C98", // Tertiary UI element color
  coolGrayAlt: "#6b7884", // Alternative cool gray
  battleshipGrayAlt: "#818581", // Alternative battleship gray
  blackOliveAlt: "#29342f", // Alternative black olive
  onyxAlt: "#333333", // Alternative onyx
  lightAccent: "#e8f0f8", // Very light steel blue for backgrounds
} as const;

/**
 * Semantic colors for consistent UI states
 */
export const SEMANTIC_COLORS = {
  success: "#39a275", // Green - matching vue.frontends
  warning: "#ea580c", // Orange
  error: "#dc2626", // Red
  info: "#406A94", // Steel Blue
  primary: "#406A94", // Steel Blue
} as const;

/**
 * Background and neutral colors for layouts
 */
export const BACKGROUND_COLORS = {
  /** Primary chart/content background */
  chart: "#ffffff",
  /** Grid lines and subtle separators */
  grid: "#f0f2f4",
  /** Subtle borders */
  border: "#e8f0f8",
  /** Primary text color */
  text: "#3C3C3C", // Onyx for better readability
} as const;

/**
 * Comprehensive neutral colors for replacing hardcoded values
 */
export const BRAND_NEUTRALS = {
  /** Pure white - #ffffff */
  white: "#ffffff",
  /** Very light gray - #f9fafb */
  veryLight: "#f9fafb",
  /** Light gray - #f0f2f4 (cadet gray very light) */
  light: "#f0f2f4",
  /** Light gray alternate - #e8f0f8 (steel blue very light) */
  lightAlt: "#e8f0f8",
  /** Subtle background - #e1e8ed (prussian blue very light) */
  subtleBackground: "#e1e8ed",
  /** Subtle border - #939fac (cadet gray light) */
  subtleBorder: "#939fac",
  /** Medium border - #7F8C98 (cadet gray base) */
  mediumBorder: "#7F8C98",
  /** Light text - #6b7884 (cadet gray dark) */
  lightText: "#6b7884",
  /** Medium text - #525252 (onyx light) */
  mediumText: "#525252",
  /** Dark text - #3C3C3C (onyx base) */
  darkText: "#3C3C3C",
  /** Darker text - #333333 (onyx dark) */
  darkerText: "#333333",
} as const;

/**
 * Utility function to get chart colors as an array
 * @param extended - Whether to include extended color palette
 * @returns Array of hex color values
 */
export function getChartColorsArray(extended = false): string[] {
  const colors = extended ? EXTENDED_CHART_COLORS : CHART_COLORS;
  return Object.values(colors);
}

/**
 * Utility function to get a specific number of colors from the palette
 * @param count - Number of colors needed
 * @returns Array of hex color values
 */
export function getChartColors(count: number): string[] {
  const allColors = getChartColorsArray(true);
  return allColors.slice(0, count);
}

/**
 * Get semantic color by type
 * @param type - The semantic type
 * @returns Hex color value
 */
export function getSemanticColor(type: keyof typeof SEMANTIC_COLORS): string {
  return SEMANTIC_COLORS[type];
}

/**
 * Brand chart colors for marketing dashboard and data visualization
 * Enterprise SaaS Design System palette
 */
export const BRAND_CHART_COLORS = [
  "#406A94", // Steel Blue (primary actions, toggles, radios)
  "#7D8CA3", // Cool Gray (charts only)
  "#969A97", // Battleship Gray (charts only)
  "#334139", // Black Olive (charts only)
  "#3C3C3C", // Onyx (charts only)
  "#0B3142", // Prussian Blue (CSP accordion headers only)
] as const;

/**
 * Warm color palette (Enterprise SaaS focused)
 * Emphasizes the Enterprise SaaS Design System colors with strategic use of supporting colors
 */
export const WARM_CHART_COLORS = [
  "#406A94", // Steel Blue (primary)
  "#7F8C98", // Cadet Gray (tertiary buttons)
  "#059669", // Success green (positive trends) - keeping semantic color
  "#ea580c", // Orange (warnings) - keeping semantic color
  "#7D8CA3", // Cool Gray (charts only)
  "#969A97", // Battleship Gray (charts only)
  "#334139", // Black Olive (charts only)
] as const;

// Type exports for better TypeScript support
export type BrandColor = keyof typeof BRAND_COLORS;
export type ChartColor = keyof typeof CHART_COLORS;
export type SemanticColor = keyof typeof SEMANTIC_COLORS;
export type BackgroundColor = keyof typeof BACKGROUND_COLORS;
export type BrandNeutral = keyof typeof BRAND_NEUTRALS;
